import { uniq, path } from 'ramda'
import { getMasterUnitParam, isSomething, getStationType, needsRainHlRemoved } from './'

const ONE_MINUTE_MS = 60000
const HOURLY_RAIN_WEIGHTS = [0.1, 0.6, 1.1, 1.4, 1.5, 1.6, 1.5, 1.4, 1.1, 0.6, 0.1]


export const GRAPH_TYPES = {
  multiDevice: {
    name: 'Multi Station',
    description: 'Compare a single type of measurement at different stations'
  },
  multiParam: {
    name: 'Multi Parameter',
    description: 'Compare different sensors at one station'
  }
}

export const getGraphMasterUnitParam = param => {
  const p = getMasterUnitParam(param) || param

  // Voltage parameters
  if (/volts/.test(p)) {
    return 'battvolts'
  }

  // Humidity parameters (%)
  if (/^humidity/.test(p) || p === 'humidityin' || p === 'humiditykestrel' || p === 'pm_in_humidity_aqin' || p === 'relativeairdensity' || p === 'pig') {
    return 'humidity'
  }

  // Wind direction parameters (°)
  if (/^winddir/.test(p) || /^windgustdir/.test(p) || /_avg\d+m$/.test(p) && p.includes('dir')) {
    return 'winddir'
  }

  // PM2.5/PM10 air quality parameters (µg/m³)
  if (/^pm25/.test(p) || /^pm10/.test(p)) {
    return 'pm25'
  }

  // CO2 parameters (ppm)
  if (/^co2/.test(p)) {
    return 'co2'
  }

  // Solar radiation parameters (W/m²)
  if (p === 'solarradiation' || p === 'twlwm2') {
    return 'solarradiation'
  }

  // AQI indices (unitless)
  if (/^aqi_/.test(p)) {
    return 'aqi'
  }

  // Leaf wetness parameters (%)
  if (/^leafwetness\d+$/.test(p)) {
    return 'leafwetness'
  }

  return p
}

export const getGraphMasterParams = (graph) => {
  const params = graph.params
    .map(({ param }) => getGraphMasterUnitParam(param))
  return uniq(params)
}

export const CACHE_DATA = true

export const GRAPH_COLORS = [
  '#15A7E2',
  '#57A709',
  '#E07D5A',
  '#9D3EC3',
  '#EA47DF',
  '#6C74E8',
  '#E5514D',
  '#99972B'
]

// Sanitize dailyrainin data to fix out-of-order issues from cached station data
export const sanitizeDailyRainData = (currentDevice, graphData) => {
  // Get timezone from device
  const tzName = path(['tz', 'name'], currentDevice)
  
  // If no timezone or no data, return original data
  if (!tzName || !graphData || graphData.length === 0) return graphData
  
  // Only apply to KestrelMet devices
  const stationType = path(['lastData', 'stationtype'], currentDevice)
  const isKestrelMet = stationType === 'NK_KestrelMet6000' || stationType === 'NK_KestrelMet6000W'
  if (!isKestrelMet) return graphData
  
  // First, sort the data by date (ascending - oldest to newest)
  const sortedData = [...graphData].sort((a, b) => a.dateutc - b.dateutc)
  
  // Process the sorted data to fix decreasing dailyrainin values within the same day
  const sanitized = sortedData.map((point, index) => {
    // Skip first point or points without dailyrainin value
    if (index === 0 || !isSomething(point.dailyrainin)) return point
    
    const prevPoint = sortedData[index - 1]
    
    // If previous point has no dailyrainin, return current point as-is
    if (!isSomething(prevPoint.dailyrainin)) {
      return point
    }
    
    const currentDate = moment.tz(point.dateutc, tzName).startOf('day')
    const prevDate = moment.tz(prevPoint.dateutc, tzName).startOf('day')
    
    // Check if same day
    if (!currentDate.isSame(prevDate)) {
      return point // Different days, no sanitization needed
    }
    
    // Same day: current dailyrainin should be >= previous dailyrainin
    // If it decreased, null it out
    if (point.dailyrainin < prevPoint.dailyrainin) {
      return { ...point, dailyrainin: null }
    }
    
    return point
  })
  
  // Create a map of sanitized values by timestamp
  const sanitizedMap = sanitized.reduce((acc, point) => {
    acc[point.dateutc] = point.dailyrainin
    return acc
  }, {})
  
  // Return data in original order but with sanitized dailyrainin values
  return graphData.map(point => ({
    ...point,
    dailyrainin: sanitizedMap[point.dateutc]
  }))
}

/**
 * Apply rolling 10-minute average to hourlyrainin for KestrelMet WiFi and IP100 stations
 *
 * For each data point, calculates the average of hourlyrainin values within the previous
 * 10 minutes (inclusive). This smooths out noise in rain rate measurements.
 *
 * @param {Object} currentDevice - Device object with stationType info
 * @param {Array} graphData - Array of data points with dateutc and hourlyrainin
 * @returns {Array} Data with averaged hourlyrainin values
 */
export const sanitizeHourlyRainDataLegacy = (currentDevice, graphData) => {
  // 1. Validate input
  if (!graphData || graphData.length === 0) return graphData
  if (!currentDevice) return graphData

  // 2. Check device type
  if (!needsRainHlRemoved(currentDevice)) {
    return graphData // Not a target station type
  }

  // 3. Sort by timestamp for correct window calculation
  const sortedData = [...graphData].sort((a, b) => a.dateutc - b.dateutc)

  // 4. Apply rolling 10-minute average
  const TEN_MINUTES_MS = 600000
  const processed = sortedData.map((point, index) => {
    const windowStart = point.dateutc - TEN_MINUTES_MS

    // Find start of window by scanning backwards
    let windowStartIdx = index
    while (windowStartIdx > 0 && sortedData[windowStartIdx - 1].dateutc >= windowStart) {
      windowStartIdx--
    }

    // Check if we have a full 10-minute window
    // If the earliest point in window is after windowStart, we don't have full 10 minutes
    const hasFullWindow = sortedData[windowStartIdx].dateutc <= windowStart

    // If we don't have a full 10-minute window, set values to null
    if (!hasFullWindow) {
      return {
        ...point,
        hourlyrainin: null,
        ...(isSomething(point.hourlyraininAvg) && { hourlyraininAvg: null })
      }
    }

    // Extract valid hourlyrainin values from window
    const windowPoints = sortedData.slice(windowStartIdx, index + 1)
    const validValues = windowPoints
      .map(p => p.hourlyrainin)
      .filter(val => isSomething(val) && !isNaN(val))

    // Calculate average
    let newHourlyRain = null
    if (validValues.length > 0) {
      const sum = validValues.reduce((acc, val) => acc + val, 0)
      newHourlyRain = Math.round(sum / validValues.length * 100) / 100
    }

    // Also process hourlyraininAvg if present
    const validAvgValues = windowPoints
      .map(p => p.hourlyraininAvg)
      .filter(val => isSomething(val) && !isNaN(val))

    let newHourlyRainAvg = point.hourlyraininAvg
    if (validAvgValues.length > 0) {
      const avgSum = validAvgValues.reduce((acc, val) => acc + val, 0)
      newHourlyRainAvg = Math.round(avgSum / validAvgValues.length * 100) / 100
    } else if (isSomething(point.hourlyraininAvg)) {
      newHourlyRainAvg = null
    }

    return {
      ...point,
      hourlyrainin: newHourlyRain,
      ...(isSomething(point.hourlyraininAvg) && { hourlyraininAvg: newHourlyRainAvg })
    }
  })

  // 5. Create map for efficient lookup and return in original order
  const processedMap = processed.reduce((acc, point) => {
    acc[point.dateutc] = point
    return acc
  }, {})

  return graphData.map(point => processedMap[point.dateutc] || point)
}

/**
 * Apply weighted 11-point centered average to hourlyrainin for KestrelMet WiFi and IP100 stations.
 *
 * For each data point, builds an 11-slot window of [t-5min .. t+5min], multiplies each slot's
 * value by the corresponding weight, sums the products, and divides by 11.
 * Missing slots (gaps in data) contribute 0. The first and last 5 minutes of the dataset are set to 0.
 */
export const sanitizeHourlyRainData = (currentDevice, graphData) => {
  if (!graphData || graphData.length === 0) return graphData
  if (!currentDevice) return graphData
  if (!needsRainHlRemoved(currentDevice)) return graphData

  const sortedData = [...graphData].sort((a, b) => a.dateutc - b.dateutc)

  const minTime = Math.round(sortedData[0].dateutc / ONE_MINUTE_MS) * ONE_MINUTE_MS
  const maxTime = Math.round(sortedData[sortedData.length - 1].dateutc / ONE_MINUTE_MS) * ONE_MINUTE_MS

  // Build lookup map keyed by rounded minute
  const byMinute = new Map()
  sortedData.forEach(point => {
    const key = Math.round(point.dateutc / ONE_MINUTE_MS) * ONE_MINUTE_MS
    byMinute.set(key, point)
  })

  const processed = sortedData.map(point => {
    const slotTime = Math.round(point.dateutc / ONE_MINUTE_MS) * ONE_MINUTE_MS
    const isEdge = slotTime < minTime + 5 * ONE_MINUTE_MS || slotTime > maxTime - 5 * ONE_MINUTE_MS

    if (isEdge) {
      return {
        ...point,
        hourlyrainin: 0,
        ...(isSomething(point.hourlyraininAvg) && { hourlyraininAvg: 0 })
      }
    }

    let weightedSum = 0
    let weightedAvgSum = 0
    for (let j = 0; j < 11; j++) {
      const neighborTime = slotTime + (j - 5) * ONE_MINUTE_MS
      const neighbor = byMinute.get(neighborTime)
      const val = (neighbor && isSomething(neighbor.hourlyrainin) && !isNaN(neighbor.hourlyrainin))
        ? neighbor.hourlyrainin : 0
      weightedSum += val * HOURLY_RAIN_WEIGHTS[j]

      if (isSomething(point.hourlyraininAvg)) {
        const avgVal = (neighbor && isSomething(neighbor.hourlyraininAvg) && !isNaN(neighbor.hourlyraininAvg))
          ? neighbor.hourlyraininAvg : 0
        weightedAvgSum += avgVal * HOURLY_RAIN_WEIGHTS[j]
      }
    }

    return {
      ...point,
      hourlyrainin: Math.round(weightedSum / 11 * 100) / 100,
      ...(isSomething(point.hourlyraininAvg) && { hourlyraininAvg: Math.round(weightedAvgSum / 11 * 100) / 100 })
    }
  })

  const processedMap = processed.reduce((acc, point) => {
    acc[point.dateutc] = point
    return acc
  }, {})

  return graphData.map(point => processedMap[point.dateutc] || point)
}

/**
 * Sanitize rain data by applying both daily and hourly rain corrections
 *
 * - Daily rain: Fixes out-of-order dailyrainin values for KestrelMet devices
 * - Hourly rain: Applies 10-minute rolling average for KestrelMet WiFi and IP100
 *
 * @param {Object} currentDevice - Device object with stationType info
 * @param {Array} graphData - Array of data points
 * @returns {Array} Data with sanitized rain values
 */
export const sanitizeRainData = (currentDevice, graphData) => {
  // Apply daily rain sanitization first
  let sanitized = sanitizeDailyRainData(currentDevice, graphData)

  // Then apply hourly rain rolling average
  sanitized = sanitizeHourlyRainData(currentDevice, sanitized)

  return sanitized
}

/**
 * Removes rain parameters (hourlyrainin, dailyrainin) entirely for specific station types
 *
 * For KestrelMet WiFi and IP100 stations, rain data is smoothed with rolling averages.
 * The backend calculates h/l on raw data, creating a mismatch with what's displayed.
 * Deleting the fields entirely prevents rendering bugs (1969 dates, "high of 0", etc.)
 * and ensures all rain rows show dashes in the summary table.
 *
 * @param {Object} currentDevice - Device object with stationType info
 * @param {Object} summaryDoc - Summary document with hl fields (e.g., { hourlyrainin: { h, l, s, c, ht, lt }, ... })
 * @returns {Object} Summary document with rain fields removed if applicable
 */
export const removeRainHlForStationType = (currentDevice, summaryDoc) => {
  if (!summaryDoc || !currentDevice) return summaryDoc

  // Check if this is a station type that needs rain hl removed
  if (!needsRainHlRemoved(currentDevice)) {
    return summaryDoc // Not a target station type
  }

  // Clone the summary doc to avoid mutation
  const cleaned = { ...summaryDoc }

  // Remove hourlyrainin and dailyrainin entirely
  if (cleaned.hourlyrainin) {
    delete cleaned.hourlyrainin
  }
  // if (cleaned.dailyrainin) {
  //   delete cleaned.dailyrainin
  // }

  return cleaned
}